/**
 * Request layer for the Alerts workspace — typed builders plus the two reads.
 *
 * All query construction lives in `buildAlertQueueParams` / `buildAlertHistoryParams`
 * so the scope rules are enforced in ONE tested place. React components never
 * assemble query strings.
 */
import type { AxiosResponse } from "axios";
import axiosClient from "@/shared/lib/axios/axios.ts";
import { API_ROUTES } from "@/alerts/api/config.ts";
import {
  unwrapEnvelope,
  toAlertApiError,
  type ApiEnvelope,
} from "@/alerts/api/envelope.ts";
import { isAlertsMockMode, mockAlertQueue, mockAlertHistory } from "@/alerts/api/mocks/alerts.mocks.ts";
import type {
  AlertHistoryFilterState,
  AlertHistoryMetadata,
  AlertHistoryPage,
  AlertHistoryParams,
  AlertQueueData,
  AlertQueueFilterState,
  AlertQueueMetadata,
  AlertQueuePage,
  AlertQueueParams,
  AlertRow,
} from "@/alerts/types/alerts-workspace.ts";

/**
 * Drop keys the caller left unset so they never reach the wire. An empty array
 * counts as unset — `type_in=` would otherwise read as "match nothing".
 */
const compact = <T extends object>(params: T): T => {
  const bag = params as Record<string, unknown>;
  for (const key of Object.keys(bag)) {
    const value = bag[key];
    if (value === undefined || value === null) delete bag[key];
    else if (Array.isArray(value) && value.length === 0) delete bag[key];
  }
  return params;
};

/**
 * Confidence is a 0..1 fraction. Anything outside is clamped rather than sent —
 * the backend would reject it, and a rejected search is indistinguishable from
 * an empty one on screen.
 */
const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

/** Arrays go on the wire comma-separated, consistently across both endpoints. */
const csv = (values?: Array<string | number>): string | undefined =>
  values && values.length > 0 ? values.join(",") : undefined;

/**
 * `metadata.next` / `metadata.previous` come back as full navigation URLs, not
 * bare tokens — feeding one straight back as `cursor=` sends the whole URL and
 * the server rejects it (or worse, silently returns page one forever).
 *
 * The extracted token stays OPAQUE: this reads the `cursor` parameter the server
 * wrote and never inspects, edits or synthesizes its contents. A value that is
 * already a bare token passes through, so both wire styles work.
 */
export const cursorFromLink = (link: string | null | undefined): string | null => {
  if (!link) return null;
  try {
    // The base only matters for relative links; it is never sent anywhere.
    const cursor = new URL(link, "http://alerts.invalid").searchParams.get("cursor");
    if (cursor) return cursor;
  } catch {
    // Not parseable as a URL — fall through to the bare-token case.
  }
  // No `cursor` parameter found. `new URL` accepts almost anything as a relative
  // path (a bare base64 token included), so the catch above cannot tell the two
  // apart — decide on shape instead. Only an absolute URL or a rooted path is a
  // navigation link, and one without a cursor genuinely has no next/previous.
  // Anything else is the token itself, which may contain `/` `+` `=` from base64.
  const isNavigationLink = /^(https?:)?\/\//i.test(link) || link.startsWith("/");
  return isNavigationLink ? null : link;
};

/**
 * Build the Queue request.
 *
 * Rules enforced here (see MIN-225 "Forbidden Queue parameters"):
 * - `location_id` is required, and is always sent — the Queue's narrower filters
 *   refine a location rather than replacing it.
 * - AT MOST ONE narrower scope reaches the wire; precedence device_mxid →
 *   device_mxids → machine_ids, so a stale second selection can never widen or
 *   contradict the request.
 * - NO date, status, ordering, offset, shift, label or confidence parameters are
 *   representable at all: model confidence and evidence labels must never hide
 *   work an operator still owes a decision on.
 */
export const buildAlertQueueParams = (state: AlertQueueFilterState): AlertQueueParams => {
  const { scope } = state;
  const params: AlertQueueParams = {
    location_id: scope.location_id,
    type_in: state.type_in,
    include_preview: state.include_preview || undefined,
    limit: state.limit,
    cursor: state.cursor ?? undefined,
  };

  // Mutually exclusive — first match wins, the rest are dropped.
  if (scope.device_mxid) params.device_mxid = scope.device_mxid;
  else if (scope.device_mxids?.length) params.device_mxids = scope.device_mxids;
  else if (scope.machine_ids?.length) params.machine_ids = scope.machine_ids;

  return compact(params);
};

/**
 * Build the History request.
 *
 * - Both time boundaries are ALWAYS sent; History is meaningless unbounded.
 * - Exactly one effective scope is sent, by precedence device → machine →
 *   location. `location_id` is never sent alongside a narrower scope.
 */
export const buildAlertHistoryParams = (
  state: AlertHistoryFilterState,
): AlertHistoryParams => {
  const { scope } = state;
  const params: AlertHistoryParams = {
    // ISO-8601 with an explicit Z. The machines module's `formatDateTime`
    // emits a naive "YYYY-MM-DD HH:mm:ss", which this API rejects with
    // `timezone_required` — it requires Z or an explicit offset.
    start_time: state.start_time.toISOString(),
    end_time: state.end_time.toISOString(),
    status_in: state.status_in,
    type_in: state.type_in,
    label_in: state.label_in,
    // Newest-first by default: a period search read oldest-first buries what
    // just happened at the bottom of the last page.
    ordering: state.ordering ?? "-timestamp",
    include_preview: state.include_preview || undefined,
    limit: state.limit,
    offset: state.offset,
  };

  // Clamped to [0,1] and re-ordered so `min` can never exceed `max` — an
  // inverted range matches nothing, which looks exactly like "no results" and
  // would send an operator hunting for data that was never excluded by content.
  const min = state.min_confidence != null ? clamp01(state.min_confidence) : undefined;
  const max = state.max_confidence != null ? clamp01(state.max_confidence) : undefined;
  if (min != null && max != null) {
    params.min_confidence = Math.min(min, max);
    params.max_confidence = Math.max(min, max);
  } else {
    params.min_confidence = min;
    params.max_confidence = max;
  }

  if (scope.device_mxid) params.device_mxid = scope.device_mxid;
  else if (scope.device_mxids?.length) params.device_mxids = scope.device_mxids;
  else if (scope.machine_ids?.length) params.machine_ids = scope.machine_ids;
  else if (scope.location_id != null) params.location_id = scope.location_id;

  return compact(params);
};

/** Serialize builder output into axios query params (arrays → CSV). */
const toQuery = (params: AlertQueueParams | AlertHistoryParams): Record<string, unknown> =>
  compact({
    ...params,
    machine_ids: csv((params as AlertQueueParams).machine_ids),
    device_mxids: csv(params.device_mxids),
    type_in: csv(params.type_in),
    status_in: csv((params as AlertHistoryParams).status_in),
    label_in: csv((params as AlertHistoryParams).label_in),
  });

/**
 * GET /api/alerts/queue/ — current Pending work in the scope.
 *
 * `metadata.next` / `metadata.previous` are preserved SEPARATELY from the domain
 * payload: they are opaque server cursors, and the only legal operations on them
 * are "send back verbatim" and "discard".
 */
export const getAlertQueue = async (
  state: AlertQueueFilterState,
  signal?: AbortSignal,
): Promise<AlertQueuePage> => {
  if (isAlertsMockMode()) return mockAlertQueue(state);
  try {
    const response = await axiosClient.get<
      unknown,
      AxiosResponse<ApiEnvelope<AlertQueueData, AlertQueueMetadata>>
    >(API_ROUTES.ALERTS_QUEUE, {
      params: toQuery(buildAlertQueueParams(state)),
      signal,
    });
    const { data, metadata } = unwrapEnvelope(response);
    return {
      items: data.results ?? [],
      // Server total for the scope — the loaded page is only a window onto it.
      count: data.count,
      asOf: data.as_of,
      summary: data.queue_summary,
      next: cursorFromLink(metadata?.next),
      previous: cursorFromLink(metadata?.previous),
    };
  } catch (error) {
    throw toAlertApiError(error);
  }
};

/** GET /api/alerts/ — bounded historical search. */
export const getAlertHistory = async (
  state: AlertHistoryFilterState,
  signal?: AbortSignal,
): Promise<AlertHistoryPage> => {
  if (isAlertsMockMode()) return mockAlertHistory(state);
  try {
    const response = await axiosClient.get<
      unknown,
      AxiosResponse<ApiEnvelope<AlertRow[], AlertHistoryMetadata>>
    >(API_ROUTES.ALERTS, {
      params: toQuery(buildAlertHistoryParams(state)),
      signal,
    });
    const { data, metadata } = unwrapEnvelope(response);
    return {
      items: data ?? [],
      // Backend-provided — never derived from the loaded rows.
      totalItems: metadata?.totalItems ?? 0,
      itemsPerPage: metadata?.itemsPerPage ?? state.limit ?? 0,
      page: metadata?.page ?? 1,
      pageCount: metadata?.pageCount ?? 0,
      truncated: metadata?.truncated ?? false,
    };
  } catch (error) {
    throw toAlertApiError(error);
  }
};
